import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosApi from "../server/service";
import toast from "react-hot-toast";
import { ArrowLeft, Plus, Search, Loader2, Factory } from "lucide-react";

// Import all the reusable components from your central UI file
import {
  Input,
  Button,
  Badge,
  ShadcnTable,
  ShadcnTableHeader,
  ShadcnTableBody,
  ShadcnTableRow,
  ShadcnTableHead,
  ShadcnTableCell,
  ActionMenu,
} from "../component/ui/TableComponent"; 

// --- TYPE DEFINITIONS ---
interface IFabricTypeData {
  id: string;
  fabricType: string;
  vendorDetails: {
    _id: string;
    vendorName: string;
  } | null;
  panna: string;
  pannaRate: number;
  agentName: string;
  status: "Active" | "Inactive";
}

// --- MAIN PAGE COMPONENT ---
const FabricTypeMaster: React.FC = () => {
  const navigate = useNavigate();
  const [fabricTypes, setFabricTypes] = useState<IFabricTypeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [itemToDelete, setItemToDelete] = useState<IFabricTypeData | null>(
    null
  );

  const totalPages = Math.ceil(totalCount / pageSize);

  const fetchFabricTypes = useCallback(
    async (pageToFetch: number) => {
      setLoading(true);
      setFetchError(null);
      const params = new URLSearchParams({
        page: String(pageToFetch),
        limit: String(pageSize),
      });
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchInput) params.append("search", searchInput);

      try {

        const { data } = await axiosApi.get(
          `/api/fabricTypes/get-all-fabrictypes?${params.toString()}`
        );
        setFabricTypes(
          data.data.fabricTypes.map((item: any) => ({ ...item, id: item._id }))
        );
        setTotalCount(data.data.totalCount);
      } catch (err: any) {
        setFetchError(
          err?.response?.data?.message || "Failed to load fabric types"
        );
      } finally {
        setLoading(false);
      }
    },
    [pageSize, statusFilter, searchInput]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      if (currentPage !== 1) setCurrentPage(1);
      else fetchFabricTypes(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput, statusFilter]);

  useEffect(() => {
    fetchFabricTypes(currentPage);
  }, [currentPage, fetchFabricTypes]);

  const handleEditClick = (id: string) => {
    navigate(`/edit-fabric-type/${id}`);
  };

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    const toastId = toast.loading("Deleting fabric type...");
    try {
    
      await axiosApi.delete(`/api/fabricTypes/delete-fabric/${itemToDelete.id}`);
      toast.success("Fabric Type deleted!", { id: toastId });
      if (fabricTypes.length === 1 && currentPage > 1)
        setCurrentPage((p) => p - 1);
      else fetchFabricTypes(currentPage);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete.", {
        id: toastId,
      });
    } finally {
      setItemToDelete(null);
    }
  }, [itemToDelete, currentPage, fabricTypes.length, fetchFabricTypes]);

  return (
    <div className="bg-slate-50 min-h-screen font-sans p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6">
        <div className="flex items-center gap-10">
          <Link
            to="/master"
            className="flex items-center gap-2 border bg-white border-slate-200 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-black font-medium">
              Back <span className="hidden sm:inline">to Masters</span>
            </span>
          </Link>
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800">
              Fabric Type Master
            </h1>
            <p className="text-slate-600 text-sm sm:text-base mt-1">
              Manage all fabric type entries
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate("/create-fabric-type")}
          className="bg-green-500 hover:bg-green-600 text-white"
        >
          <Plus className="w-5 h-5 mr-2" /> New Fabric Type
        </Button>
      </div>

      <div className="mt-6 bg-white shadow-md rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, vendor, agent..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full md:w-48 h-10 border border-slate-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="mt-6 bg-white shadow-lg rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center gap-2 p-12 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading Fabric Types...</span>
          </div>
        ) : fetchError ? (
          <div className="p-12 text-center text-red-600 font-medium">
            {fetchError}
          </div>
        ) : fabricTypes.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            No fabric types found.
          </div>
        ) : (
          <>
            <div className="hidden lg:block">
              <ShadcnTable>
                <ShadcnTableHeader>
                  <ShadcnTableRow>
                    <ShadcnTableHead>Fabric Name/Type</ShadcnTableHead>
                    <ShadcnTableHead>Vendor</ShadcnTableHead>
                    <ShadcnTableHead>Panna</ShadcnTableHead>
                    <ShadcnTableHead>Panna Rate</ShadcnTableHead>
                    <ShadcnTableHead>Status</ShadcnTableHead>
                    <ShadcnTableHead className="text-center">
                      Actions
                    </ShadcnTableHead>
                  </ShadcnTableRow>
                </ShadcnTableHeader>
                <ShadcnTableBody>
                  {fabricTypes.map((item) => (
                    <ShadcnTableRow key={item.id}>
                      <ShadcnTableCell className="font-medium text-slate-900">
                        {item.fabricType}
                      </ShadcnTableCell>
                      <ShadcnTableCell>
                        {item.vendorDetails?.vendorName || "N/A"}
                      </ShadcnTableCell>
                      <ShadcnTableCell>{item.panna || "N/A"}</ShadcnTableCell>
                      <ShadcnTableCell>
                        ₹{item.pannaRate?.toLocaleString() || "0"}
                      </ShadcnTableCell>
                      <ShadcnTableCell>
                        <Badge
                          variant={
                            item.status === "Active" ? "success" : "danger"
                          }
                        >
                          {item.status}
                        </Badge>
                      </ShadcnTableCell>
                      <ShadcnTableCell className="flex justify-center items-center">
                        <ActionMenu
                          onEdit={() => handleEditClick(item.id)}
                          onDelete={() => setItemToDelete(item)}
                        />
                      </ShadcnTableCell>
                    </ShadcnTableRow>
                  ))}
                </ShadcnTableBody>
              </ShadcnTable>
            </div>
            {/* Mobile Card View would go here */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 lg:hidden">
              {fabricTypes.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow-md border border-gray-300 p-4 flex flex-col justify-between"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 bg-slate-100 p-3 rounded-full">
                        <Factory className="text-gray-500 w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-slate-900">
                          {item.fabricType}
                        </h3>
                        <p className="text-sm text-slate-600">
                          {item.vendorDetails?.vendorName || "N/A"}
                        </p>
                      </div>
                    </div>
                    <ActionMenu
                      onEdit={() => handleEditClick(item.id)}
                      onDelete={() => setItemToDelete(item)}
                    />
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Status:</span>
                      <Badge
                        variant={
                          item.status === "Active" ? "success" : "danger"
                        }
                      >
                        {item.status}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Panna:</span>
                      <span className="font-medium text-slate-800">
                        {item.panna || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Rate:</span>
                      <span className="font-medium text-slate-800">
                        ₹{item.pannaRate?.toLocaleString() || "0"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between flex-wrap gap-4 p-4 border-t">
                <span className="text-sm text-slate-600">
                  Showing{" "}
                  <span className="font-medium">{fabricTypes.length}</span> of{" "}
                  <span className="font-medium">{totalCount}</span> results
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage === 1 || loading}
                  >
                    Previous
                  </Button>
                  <span className="text-sm font-medium px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage >= totalPages || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md m-4 p-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Confirm Deletion
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete fabric type:{" "}
              <span className="font-bold">{itemToDelete.fabricType}</span>?
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setItemToDelete(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FabricTypeMaster;
